import {
	faCheckCircle,
	faChevronDown,
	faChevronUp,
	faExclamationTriangle,
	faSpinner,
	faStar,
	faTimesCircle
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import defaultPspLogo from "@/assets/default-psp.png";
import SidePanel from "@/components/Sidepanel";
import { useAuthStore } from "@/store/auth.store";
import { useEnvironmentStore } from "@/store/environments.store";
import { IFlowAction, IFlowTarget, useFlowStore } from "@/store/flow.store";
import { type ApiError, handleApiError } from "@/utils/errorHandler";
import ViewFlow, { getFlows } from "./ViewFlow";

interface FlowType {
	id: string;
	name: string;
}

// Flow Target interface from the second file
export interface FlowTarget {
	id: string;
	name: string;
	status: string;
	credentialSchema: Record<string, unknown>;
	inputSchema: Record<string, unknown>;
	flowTypeId: string;
	logo: string | null;
	createdAt: string;
	updatedAt: string;
	brandId?: string;
	trustScore?: number;
}

const getTargetStatus = (target: IFlowTarget) => {
	const hasActiveFlow = target.actions?.some(
		(action) => action.definition?.flow?.status
	);
	return hasActiveFlow ? "ENABLED" : "DISABLED";
};

const getFlowTargetStatus = (target: FlowTarget) => {
	return target.status === "ENABLED" ? "ENABLED" : "DISABLED";
};

const ServiceProviderCard = ({
	target,
	isRecommended = false,
	isFlowTarget = false,
	onPanelClose
}: {
	target: IFlowTarget | FlowTarget;
	isRecommended?: boolean;
	isFlowTarget?: boolean;
	onPanelClose?: () => void;
}) => {
	const { brandId } = useAuthStore();

	const targetStatus = isFlowTarget
		? getFlowTargetStatus(target as FlowTarget)
		: getTargetStatus(target as IFlowTarget);
	const isActive = targetStatus === "ENABLED";

	const trustScore: number = target.trustScore ?? 90;
	const rating = Math.round((trustScore / 20 + Number.EPSILON) * 10) / 10;

	const hasActions = !isFlowTarget && (target as IFlowTarget).actions;
	const actions = hasActions ? (target as IFlowTarget).actions : [];

	const [isExpanded, setIsExpanded] = useState(false);
	const [isPanelOpen, setIsPanelOpen] = useState(false);
	const actionsToDisplay =
		!isExpanded && actions.length > 2 ? actions.slice(0, 1) : actions;
	const remainingCount = actions.length - 1;

	const handleCardClick = () => {
		setIsPanelOpen(true);
	};

	const handlePanelClose = () => {
		setIsPanelOpen(false);
		// Trigger refresh callback when panel closes
		if (onPanelClose) {
			onPanelClose();
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={handleCardClick}
				className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer p-6 relative focus:outline-none focus:ring-2 focus:ring-blue-500"
			>
				{/* Recommended Badge */}
				{isRecommended && isActive && (
					<div className="absolute top-2 left-2">
						<div className="flex items-center bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
							<FontAwesomeIcon icon={faStar} className="w-3 h-3 mr-1" />
							Top Pick
						</div>
					</div>
				)}

				{/* Status Badge */}
				{isActive ? (
					<div className="absolute top-4 right-4">
						<span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
							Active
						</span>
					</div>
				) : (
					<div className="absolute top-4 right-4">
						<span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
							Inactive
						</span>
					</div>
				)}

				{/* Logo and Header */}
				<div className="flex flex-col items-center text-center mb-4">
					<div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
						{target.logo?.startsWith("data:image") ? (
							<img
								src={target.logo}
								alt={target.name}
								className="w-12 h-12 object-contain"
							/>
						) : (
							<img
								src={defaultPspLogo}
								alt="Default logo"
								className="w-12 h-12 object-contain"
							/>
						)}
					</div>
					<h3 className="text-lg font-semibold text-gray-900 mb-1">
						{target.name}
					</h3>

					{/* Rating */}
					<div className="flex items-center text-sm text-gray-600 mb-2">
						<FontAwesomeIcon
							icon={faStar}
							className="w-3 h-3 text-yellow-400 mr-1"
						/>
						<span className="font-medium">{rating.toFixed(1)}</span>
					</div>
				</div>

				{/* Flow Status Indicators - Only for flow targets with actions */}
				{hasActions && actions.length > 0 && (
					<div className="space-y-2 mb-4">
						{actionsToDisplay.map((action: IFlowAction) => (
							<div
								key={action.id}
								className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
							>
								<span className="font-medium text-gray-700">{action.name}</span>
								<div className="flex items-center space-x-2">
									{action.definition?.flow?.status === "ENABLED" ? (
										<>
											<FontAwesomeIcon
												icon={faCheckCircle}
												className="h-4 w-4 text-green-500"
											/>
											<span className="text-green-600 font-medium">Active</span>
										</>
									) : (
										<>
											<FontAwesomeIcon
												icon={faTimesCircle}
												className="h-4 w-4 text-gray-400"
											/>
											<span className="text-gray-500">Inactive</span>
										</>
									)}
								</div>
							</div>
						))}

						{/* Show more / less toggle */}
						{actions.length > 2 && (
							<button
								type="button"
								onClick={() => setIsExpanded((v) => !v)}
								className="w-full flex items-center justify-center text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
							>
								{isExpanded ? (
									<>
										Show less{" "}
										<FontAwesomeIcon
											icon={faChevronUp}
											className="ml-1 w-3 h-3"
										/>
									</>
								) : (
									<>
										Show {remainingCount} more{" "}
										<FontAwesomeIcon
											icon={faChevronDown}
											className="ml-1 w-3 h-3"
										/>
									</>
								)}
							</button>
						)}
					</div>
				)}

				{/* Trust Score */}
				<div className="mt-4">
					<div className="flex items-center justify-between text-xs text-gray-600 mb-1">
						<span>Trust Score</span>
						<span className="font-semibold">{trustScore}%</span>
					</div>
					<div className="w-full h-1.5 bg-gray-200 rounded">
						<div
							style={{ width: `${Math.min(Math.max(trustScore, 0), 100)}%` }}
							className="h-full bg-green-500 rounded"
						/>
					</div>
				</div>
			</button>

			<SidePanel
				isOpen={isPanelOpen}
				onClose={handlePanelClose}
				title={target.name}
				closeOnOutsideClick={false}
			>
				<ViewFlow
					brandId={brandId || ""}
					flowTypeId={target.flowTypeId || ""}
					flowTargetId={target.id || ""}
					setIsPanelOpen={handlePanelClose}
				/>
			</SidePanel>
		</>
	);
};

const getFlowTypes = async (): Promise<FlowType[]> => {
	const response = await apiClient.get(
		resolveEndpoint(API_ENDPOINTS, ["FLOW_TYPES", "LIST"])
	);
	return response.data;
};

const getFlowTargets = async (flowTypeId: string): Promise<FlowTarget[]> => {
	try {
		const { data } = await apiClient.get(
			resolveEndpoint(
				API_ENDPOINTS,
				["FLOW_TARGETS", "LIST_BY_BRAND"],
				flowTypeId
			)
		);
		return data as FlowTarget[];
	} catch (error) {
		handleApiError(error as ApiError, "Failed to fetch flow targets");
		throw error;
	}
};

const BRAND_FLOW_TARGETS_QUERY_KEY = (flowTypeId: string, brandId: string) =>
	[`brand-flow-targets-${brandId}`, flowTypeId] as const;

const FlowTargetPage: React.FC = () => {
	const { brandId } = useAuthStore();
	const { selectedEnvironmentSecret } = useEnvironmentStore();
	const {
		selectedFlowTypeName,
		selectedFlowTypeId,
		flows,
		setSelectedFlowTypeName,
		setSelectedFlowTypeId,
		setFlows,
		setIsLoading
	} = useFlowStore();

	const queryClient = useQueryClient();
	const [isLoadingFlows, setIsLoadingFlows] = useState(false);
	const [isError, setIsError] = useState(false);

	const { data: flowTypes = [], isLoading: isLoadingFlowTypes } = useQuery({
		queryKey: [`brand-flow-types-${brandId}`],
		queryFn: async () => {
			try {
				return await getFlowTypes();
			} catch (error) {
				handleApiError(error as ApiError, "Failed to load flow types");
				return [];
			}
		}
	});

	const {
		data: flowTargets = [],
		isLoading: isLoadingFlowTargets,
		isError: isFlowTargetsError
	} = useQuery({
		queryKey: BRAND_FLOW_TARGETS_QUERY_KEY(
			selectedFlowTypeId || "",
			brandId || ""
		),
		queryFn: () => getFlowTargets(selectedFlowTypeId || ""),
		enabled: !!selectedFlowTypeId && !!brandId,
		refetchOnWindowFocus: false,
		staleTime: 0,
		refetchOnMount: true
	});

	useEffect(() => {
		if (flowTypes.length > 0 && !selectedFlowTypeName) {
			setSelectedFlowTypeName(flowTypes[0].name);
			setSelectedFlowTypeId(flowTypes[0].id);
		}
	}, [
		flowTypes,
		selectedFlowTypeName,
		setSelectedFlowTypeName,
		setSelectedFlowTypeId
	]);

	const fetchFlows = async () => {
		if (
			!selectedEnvironmentSecret ||
			flowTypes.length === 0 ||
			!selectedFlowTypeName
		) {
			return;
		}

		try {
			setIsLoadingFlows(true);
			setIsLoading(true);
			setIsError(false);

			const data = await getFlows({
				brandId: brandId as string,
				environmentId: selectedEnvironmentSecret as string,
				type: selectedFlowTypeName as string
			});

			const newFlowsData = JSON.parse(JSON.stringify(data));
			setFlows(newFlowsData);
		} catch (error) {
			setIsError(true);
			handleApiError(error as ApiError, "Failed to load flows");
		} finally {
			setIsLoadingFlows(false);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (flowTypes.length > 0 && !selectedFlowTypeName) {
			setSelectedFlowTypeName(flowTypes[0].name);
			setSelectedFlowTypeId(flowTypes[0].id);
		}
	}, [
		flowTypes,
		selectedFlowTypeName,
		setSelectedFlowTypeName,
		setSelectedFlowTypeId
	]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally ignoring missing deps
	useEffect(() => {
		fetchFlows();
	}, [
		brandId,
		selectedEnvironmentSecret,
		selectedFlowTypeName,
		flowTypes,
		setFlows,
		setIsLoading
	]);

	// Function to handle panel close and refresh data
	const handlePanelClose = async () => {
		// Invalidate flow targets query to refetch data
		await queryClient.invalidateQueries({
			queryKey: BRAND_FLOW_TARGETS_QUERY_KEY(
				selectedFlowTypeId || "",
				brandId || ""
			)
		});

		// Refetch flows data
		await fetchFlows();
	};

	const allTargets = flows?.flatMap((flow) => flow.targets) || [];

	const activeTargets = allTargets.filter(
		(target: IFlowTarget) => getTargetStatus(target) === "ENABLED"
	);

	const sortedByTrust = [...activeTargets].sort(
		(a, b) => (b.trustScore ?? 0) - (a.trustScore ?? 0)
	);
	const topPickIds = new Set(sortedByTrust.slice(0, 3).map((t) => t.id));

	const allFlowTargets = flowTargets || [];

	const activeTargetIds = new Set(activeTargets.map((t) => t.id));

	const remainingFlowTargets = allFlowTargets.filter(
		(t) => !activeTargetIds.has(t.id)
	);

	if (isLoadingFlowTypes) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	return (
		<div className="flex-1 bg-gray-50 min-h-screen">
			<div className="max-w-7xl mx-auto px-4 py-8">
				{/* Flow Type Filter Tabs */}
				<div className="mb-8">
					<div className="flex flex-wrap gap-2 justify-left">
						{flowTypes.map((type) => (
							<button
								type="button"
								key={type.id}
								onClick={() => {
									setSelectedFlowTypeName(type.name);
									setSelectedFlowTypeId(type.id);
								}}
								className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
									selectedFlowTypeName === type.name
										? "bg-blue-600 text-white"
										: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
								}`}
							>
								{type.name}
							</button>
						))}
					</div>
				</div>

				{/* No Flow Types Message */}
				{!isLoadingFlowTypes && flowTypes.length === 0 && (
					<div className="bg-white shadow-sm rounded-lg p-8 text-center">
						<div className="flex flex-col items-center justify-center">
							<FontAwesomeIcon
								icon={faExclamationTriangle}
								className="h-12 w-12 text-yellow-500 mb-4"
							/>
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								No Integration Available
							</h3>
							<p className="text-gray-600 mb-4">
								You need to create flow types before you can view flows.
							</p>
						</div>
					</div>
				)}

				{/* Loading State */}
				{(isLoadingFlows || isLoadingFlowTargets) && (
					<div className="flex justify-center items-center h-64">
						<FontAwesomeIcon
							icon={faSpinner}
							spin
							className="h-8 w-8 text-blue-500"
						/>
					</div>
				)}

				{/* Error State */}
				{(isError || isFlowTargetsError) && (
					<div className="flex items-center justify-center h-64 text-red-600">
						Error: Failed to load data
					</div>
				)}

				{/* Targets Grid */}
				{!isLoadingFlows &&
					!isLoadingFlowTargets &&
					!isError &&
					!isFlowTargetsError && (
						<>
							{/* Active Flows Section */}
							{activeTargets.length > 0 && (
								<div className="mb-12">
									<div className="flex items-center mb-6">
										<FontAwesomeIcon
											icon={faStar}
											className="text-orange-500 mr-2"
										/>
										<h2 className="text-xl font-semibold text-gray-900">
											Active Flows
										</h2>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
										{activeTargets.map((target: IFlowTarget) => (
											<ServiceProviderCard
												key={target.id}
												target={target}
												isRecommended={topPickIds.has(target.id)}
												isFlowTarget={false}
												onPanelClose={handlePanelClose}
											/>
										))}
									</div>
								</div>
							)}

							{/* Available Service Providers Section */}
							<div className="mb-12">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-xl font-semibold text-gray-900">
										Available Service Providers
									</h2>
								</div>
								{remainingFlowTargets.length > 0 ? (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
										{remainingFlowTargets.map((target: FlowTarget) => (
											<ServiceProviderCard
												key={target.id}
												target={target}
												isFlowTarget={true}
												onPanelClose={handlePanelClose}
											/>
										))}
									</div>
								) : (
									<div className="bg-white shadow-sm rounded-lg p-8 text-center">
										<div className="flex flex-col items-center justify-center">
											<FontAwesomeIcon
												icon={faExclamationTriangle}
												className="h-12 w-12 text-yellow-500 mb-4"
											/>
											<h3 className="text-lg font-medium text-gray-900 mb-2">
												No Service Providers Found
											</h3>
											<p className="text-gray-600 text-sm text-center max-w-md">
												There are currently no service providers configured for
												this environment.
											</p>
										</div>
									</div>
								)}
							</div>
						</>
					)}
			</div>
		</div>
	);
};

export default FlowTargetPage;
